import { useEffect, useMemo, useRef, useState } from "react"
import { getViewport, useViewport } from "./GlobalViewState"

export type MarkerPoint<T> = {
    type: "point"
    item: T
    longitude: number
    latitude: number
    animationOffsetPx: [number, number] | null
}

export type MarkerCluster<T> = {
    type: "cluster"
    id: string
    items: T[]
    longitude: number
    latitude: number
}

export type LeavingMarker<T> = {
    type: "leaving"
    item: T
    longitude: number
    latitude: number
    targetOffsetPx: [number, number]
}

export type ClusteredMarker<T> = MarkerPoint<T> | MarkerCluster<T> | LeavingMarker<T>

export const useClusteredMarkers = <T>({
    items,
    getPosition,
    getKey,
    radiusPx = 60,
}: {
    items: T[]
    getPosition: (item: T) => [number, number]
    getKey: (item: T) => string
    radiusPx?: number
}) => {
    const viewport = useViewport()
    const zoomLevel = Math.round(viewport.zoom)

    const clusterOriginRef = useRef<Map<string, [number, number]>>(new Map())
    const prevPointRef = useRef<Map<string, { item: T; longitude: number; latitude: number }>>(
        new Map()
    )
    const leavingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
    const [leavingItems, setLeavingItems] = useState<LeavingMarker<T>[]>([])

    const clustered = useMemo(() => {
        if (!items.length) return []
        void zoomLevel // allows zoomLevel to be in the deps array
        const vp = getViewport()
        const projected = items.map((item) => {
            const [lng, lat] = getPosition(item)
            const px = vp.project([lng, lat]) as [number, number]
            return { item, lng, lat, px }
        })

        type RawCluster = {
            members: typeof projected
            sumPx: [number, number]
        }

        const rawClusters: RawCluster[] = []

        for (const point of projected) {
            let assigned = false
            for (const cluster of rawClusters) {
                const count = cluster.members.length
                const cx = cluster.sumPx[0] / count
                const cy = cluster.sumPx[1] / count
                const dx = point.px[0] - cx
                const dy = point.px[1] - cy
                if (Math.sqrt(dx * dx + dy * dy) < radiusPx) {
                    cluster.members.push(point)
                    cluster.sumPx[0] += point.px[0]
                    cluster.sumPx[1] += point.px[1]
                    assigned = true
                    break
                }
            }
            if (!assigned) {
                rawClusters.push({
                    members: [point],
                    sumPx: [point.px[0], point.px[1]],
                })
            }
        }

        return rawClusters.map((cluster, i): ClusteredMarker<T> => {
            if (cluster.members.length === 1) {
                const { item, lng, lat } = cluster.members[0]
                const origin = clusterOriginRef.current.get(getKey(item))
                let animationOffsetPx: [number, number] | null = null

                if (origin) {
                    const clPx = vp.project(origin) as [number, number]
                    const itemPx = vp.project([lng, lat]) as [number, number]
                    animationOffsetPx = [clPx[0] - itemPx[0], clPx[1] - itemPx[1]]
                }
                return { type: "point", item, longitude: lng, latitude: lat, animationOffsetPx }
            }

            const count = cluster.members.length
            const cx = cluster.sumPx[0] / count
            const cy = cluster.sumPx[1] / count
            const [longitude, latitude] = vp.unproject([cx, cy]) as [number, number]

            return {
                type: "cluster",
                id: `${i}`,
                items: cluster.members.map((m) => m.item),
                longitude,
                latitude,
            }
        })
    }, [items, radiusPx, getPosition, getKey, zoomLevel])

    useEffect(() => {
        const currentPointKeys = new Set<string>()
        const newClusterOrigins = new Map<string, [number, number]>()

        for (const marker of clustered) {
            if (marker.type === "point") {
                currentPointKeys.add(getKey(marker.item))
            } else if (marker.type === "cluster") {
                for (const item of marker.items) {
                    newClusterOrigins.set(getKey(item), [marker.longitude, marker.latitude])
                }
            }
        }

        clusterOriginRef.current = newClusterOrigins

        const vp = getViewport()
        const newLeaving: LeavingMarker<T>[] = []
        for (const [key, prev] of prevPointRef.current) {
            if (!currentPointKeys.has(key)) {
                const clusterOrigin = newClusterOrigins.get(key)
                if (clusterOrigin) {
                    const itemPx = vp.project([prev.longitude, prev.latitude]) as [number, number]
                    const clPx = vp.project(clusterOrigin) as [number, number]
                    newLeaving.push({
                        type: "leaving",
                        item: prev.item,
                        longitude: prev.longitude,
                        latitude: prev.latitude,
                        targetOffsetPx: [clPx[0] - itemPx[0], clPx[1] - itemPx[1]],
                    })
                }
            }
        }

        const nextPrevPoints = new Map<string, { item: T; longitude: number; latitude: number }>()
        for (const marker of clustered) {
            if (marker.type === "point") {
                nextPrevPoints.set(getKey(marker.item), {
                    item: marker.item,
                    longitude: marker.longitude,
                    latitude: marker.latitude,
                })
            }
        }
        prevPointRef.current = nextPrevPoints

        setLeavingItems((prev) => {
            const stillLeaving = prev.filter((l) => !currentPointKeys.has(getKey(l.item)))
            if (!newLeaving.length) return stillLeaving.length === prev.length ? prev : stillLeaving
            const existingKeys = new Set(stillLeaving.map((l) => getKey(l.item)))
            const toAdd = newLeaving.filter((n) => !existingKeys.has(getKey(n.item)))
            return [...stillLeaving, ...toAdd]
        })

        for (const leaving of newLeaving) {
            const key = getKey(leaving.item)
            const existing = leavingTimersRef.current.get(key)
            if (existing) clearTimeout(existing)
            const timer = setTimeout(() => {
                setLeavingItems((prev) => prev.filter((l) => getKey(l.item) !== key))
                leavingTimersRef.current.delete(key)
            }, 400)
            leavingTimersRef.current.set(key, timer)
        }
    }, [clustered, getKey])

    useEffect(() => {
        const timers = leavingTimersRef.current
        return () => timers.forEach(clearTimeout)
    }, [])

    return [...clustered, ...leavingItems]
}
